import type { Field, FieldGroup } from './types';
import { get } from 'lodash';

// Helper function to get field value from data object
export function getFieldValue(
  data: Record<string, unknown>,
  field: string,
): unknown {
  const keys = field.split('.');
  let value: unknown = data;

  for (const key of keys) {
    if (typeof value !== 'object' || value === null) {
      return undefined;
    }
    value = (value as Record<string, unknown>)[key];
  }

  return value;
}

// Helper function to format field value based on type and formatter
export function formatFieldValue(value: unknown, type?: string): string {
  if (value === null || value === undefined) {
    return '';
  }

  switch (type) {
    case 'date':
      return new Date(value as string).toLocaleDateString();
    case 'datetime':
      return new Date(value as string).toLocaleString();
    case 'number':
      return (value as number).toLocaleString();
    case 'json':
      return JSON.stringify(value, null, 2);
    case 'list':
      return Array.isArray(value) ? value.join(', ') : String(value);
    default:
      return String(value);
  }
}

// Helper function to check if a field is empty
export function isFieldEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
}

// Helper function to get all fields from a group
export function getAllFields(group: FieldGroup): Field[] {
  return group.fields.reduce<Field[]>((acc, field) => {
    if ('field' in field) {
      acc.push(field);
    } else {
      acc.push(...getAllFields(field));
    }
    return acc;
  }, []);
}

// Helper function to check if a field should be shown
export function shouldShowField(
  field: Field,
  value: unknown,
  showEmptyFields: boolean,
): boolean {
  if (showEmptyFields) {
    return true;
  }

  return !isFieldEmpty(value);
}

// Helper function to get field tooltip
export function getFieldTooltip(
  field: Field,
  value: unknown,
): string | undefined {
  if (field.tooltip) {
    return field.tooltip;
  }

  if (field.type === 'json') {
    return 'Click to expand';
  }

  if (field.isExpandable && typeof value === 'string' && value.length > 100) {
    return 'Click to expand';
  }

  return undefined;
}

// Helper function to truncate text
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}...`;
}

// Helper function to get field container class names
export function getFieldContainerClassNames(
  field: Field,
  layout: 'row' | 'column' = 'row',
): string {
  const classes = ['flex'];

  if (layout === 'row') {
    classes.push('items-center gap-2');
  } else {
    classes.push('flex-col gap-1');
  }

  if (field.containerClassName) {
    classes.push(field.containerClassName);
  }

  return classes.join(' ');
}

// Helper function to get field label class names
export function getFieldLabelClassNames(field: Field): string {
  const classes = ['text-sm font-medium text-gray-700'];

  if (field.labelClassName) {
    classes.push(field.labelClassName);
  }

  return classes.join(' ');
}

// Helper function to get field value class names
export function getFieldValueClassNames(field: Field): string {
  const classes = ['text-sm text-gray-900'];

  if (field.className) {
    classes.push(field.className);
  }

  return classes.join(' ');
}

/**
 * Process a template string with JSON path expressions
 * Example: "Risk {{description}} for {{riskTypeId}}"
 * Also supports nested paths: "{{meta.lastUpdated}}"
 * And array access: "{{factors[0]}}"
 */
export function processTemplate(
  template: string,
  data: Record<string, unknown>,
): string {
  if (!template) {
    return '';
  }

  return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    // Trim whitespace from the path
    const trimmedPath = path.trim();

    // Handle nested paths and array access
    const value = get(data, trimmedPath);

    // If value is undefined or null, return empty string
    if (value === undefined || value === null) {
      return '';
    }

    // Convert to string and handle arrays/objects
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  });
}
